import ColorUtils from './ColorUtils';
import DrawingUtils from './DrawingUtils';
import { ChartScaleViewModel, ItoDrawingViewModel, MathExt } from './ChartEngine';
import DrawDirection from './DrawDirection';

const SasiDirection = {
    UP: 1,
    DOWN: -1, 
};

export interface DrawOption {
    chartWidth: number,
    scaleTop?: number,
    scaleBottom?: number,
    kagariTop: number,
    kagariBottom: number,
    drawScale: boolean,
    backgroundColor?: string,
    resolution: number,
    step: number,
}

function draw(canvas: HTMLCanvasElement,
              chartScale: ChartScaleViewModel,
              itoDrawings: Array<ItoDrawingViewModel>,
              option: DrawOption) {
    let marginHorizontal: number = (canvas.width - option.chartWidth) / 2.0;
    let chartWidth: number = option.chartWidth;
    let scaleTop: number = option.scaleTop ? option.scaleTop : 0
    let scaleBottom: number = option.scaleBottom ? option.scaleBottom : 0
    let scaleTextMarginTop: number = 20;
    let arrowMarginBottom: number = 2;
    let arrowMarginHorizontal: number = 4;
    let arrowHeight: number = 12;
    let arrowStackingHeight: number = 4;
    let arrowHatWidth: number = 4;
    let arrowHatHeightHalf: number = 6;
    let arrowTextMarginBottom: number = -2;
    let kagariTop: number = option.kagariTop;
    let kagariBottom: number = option.kagariBottom;
    let backgroundColor: string = option.backgroundColor ? option.backgroundColor : "white";
    let resolution: number = option.resolution;
    let step: number = option.step;

    function drawScale(chartScale: ChartScaleViewModel, context: CanvasRenderingContext2D) {
        let komaWidth = chartWidth / chartScale.koma;
        let left = marginHorizontal;
        let textTop = scaleBottom + scaleTextMarginTop;

        context.beginPath();
        DrawingUtils.setStrokeStyle("black", context);
        context.fillStyle = "black";
        context.font = "14px serif";
        context.lineWidth = 2.0;
        for (var i = 0; i <= chartScale.koma; i++) {
            let x = left + komaWidth * i;
            DrawingUtils.drawLine({x: x, y: scaleTop}, {x: x, y: scaleBottom}, context);
            if (i <= chartScale.tobi) {
                DrawingUtils.fillText(i.toString(), {x: x - 4, y: textTop}, context);
            }
        }
        context.stroke();

        let arrowBottomBase = scaleTop - arrowMarginBottom;
        let arrowTop = -arrowHeight;
        let arrowBottom = 0;

        for (var j = 0; j < chartScale.sasiMukis.length; j++) {
            let sasiMuki = chartScale.sasiMukis[j];
            if (sasiMuki.width <= 0.0) {
                continue;
            }

            let arrowWidth = komaWidth * sasiMuki.width - arrowMarginHorizontal * 2;
            let offsetX = marginHorizontal + sasiMuki.boxIndex * komaWidth
                + (sasiMuki.direction === DrawDirection.RIGHT ? arrowMarginHorizontal 
                    : komaWidth - arrowWidth - arrowMarginHorizontal);
            let offsetY = arrowBottomBase - sasiMuki.stack * arrowStackingHeight;

            context.beginPath();

            DrawingUtils.setStrokeStyle(ColorUtils.formatColor(sasiMuki.color), context);

            if (sasiMuki.direction === DrawDirection.RIGHT) {
                DrawingUtils.drawLines([
                    { x: offsetX, y: offsetY + arrowBottom },
                    { x: offsetX, y: offsetY + arrowTop },
                    { x: offsetX + arrowWidth, y: offsetY + arrowTop },
                ], context, false);
                DrawingUtils.drawLines([
                    { x: offsetX + arrowWidth - arrowHatWidth, y: offsetY + arrowTop - arrowHatHeightHalf },
                    { x: offsetX + arrowWidth, y: offsetY + arrowTop },
                    { x: offsetX + arrowWidth - arrowHatWidth, y: offsetY + arrowTop + arrowHatHeightHalf },
                ], context, false);
            } else {
                DrawingUtils.drawLines([
                    { x: offsetX, y: offsetY + arrowTop },
                    { x: offsetX + arrowWidth, y: offsetY + arrowTop },
                    { x: offsetX + arrowWidth, y: offsetY + arrowBottom },
                ], context, false);
                DrawingUtils.drawLines([
                    { x: offsetX + arrowHatWidth, y: offsetY + arrowTop - arrowHatHeightHalf },
                    { x: offsetX, y: offsetY + arrowTop },
                    { x: offsetX + arrowHatWidth, y: offsetY + arrowTop + arrowHatHeightHalf },
                ], context, false);
            }

            context.font = "16px serif";
            context.fillStyle = ColorUtils.formatColor(sasiMuki.color);
            context.textAlign = "center";
            context.lineWidth = 2.0;
            DrawingUtils.fillText(`[${sasiMuki.orderNumber}]`,
                { x: offsetX + arrowWidth / 2, y: offsetY + arrowTop - arrowTextMarginBottom - 8},
                context);

            context.stroke();
        }
    }

    function drawChart(itoDrawings: Array<ItoDrawingViewModel>, koma: number, tobi: number, resolution: number, context: CanvasRenderingContext2D) {
        let komaWidth = chartWidth / koma;
        let sasiWidth = komaWidth / resolution;
        let lineCount = MathExt.lcd(koma, tobi) / tobi * 2;
        let availableDrawings = Math.min(step, itoDrawings.length);
        let lineWidth = Math.max(1.0, Math.floor(sasiWidth * 0.8));

        for (var i = 0; i < availableDrawings; i++) {
            let itoDrawing = itoDrawings[i];
            var direction = SasiDirection.DOWN;

            let delta = (itoDrawing.deltaIndex + 0.5) * sasiWidth;
            let offsetAddition = (itoDrawing.direction === DrawDirection.RIGHT) ? tobi / 2 : tobi / -2;
            let offsetMax = koma;
            var offset = itoDrawing.offset;

            context.beginPath();
            context.lineWidth = lineWidth;

            DrawingUtils.moveTo({ x: marginHorizontal + offset * komaWidth + delta, y: kagariTop }, context);
            for (var j = 0; j <= lineCount; j++) {
                offset += offsetAddition;
                var x: number = marginHorizontal + offset * komaWidth + delta;
                if (direction === SasiDirection.DOWN) {
                    DrawingUtils.lineTo({ x: x, y: kagariBottom }, context);
                } else {
                    DrawingUtils.lineTo({ x: x, y: kagariTop }, context);
                }

                var oneMore = false;
                if (itoDrawing.direction === DrawDirection.LEFT && x < marginHorizontal) {
                    offset += offsetMax;
                    x += chartWidth;
                    oneMore = true;
                } else if (itoDrawing.direction === DrawDirection.RIGHT && marginHorizontal + chartWidth <= x) {
                    offset -= offsetMax;
                    x -= chartWidth;
                    oneMore = true;
                }

                if (oneMore) {
                    if (direction === SasiDirection.DOWN) {
                        DrawingUtils.drawLine(
                            { x: x - offsetAddition * komaWidth, y: kagariTop },
                            { x: x, y: kagariBottom},
                            context
                        );
                    } else {
                        DrawingUtils.drawLine(
                            { x: x - offsetAddition * komaWidth, y: kagariBottom},
                            { x: x, y: kagariTop },
                            context
                        );
                    }
                }

                direction = (direction === SasiDirection.DOWN) ? SasiDirection.UP : SasiDirection.DOWN;
            }
            
            DrawingUtils.setStrokeStyle(ColorUtils.formatColor(itoDrawing.color), context);
            context.stroke();
        }
    }

    function cutoffSide(context: CanvasRenderingContext2D) {
        context.beginPath();
        context.fillStyle = backgroundColor;
        let height = scaleBottom - scaleTop;
        context.fillRect(0, scaleTop - 1, marginHorizontal - 1, height);
        context.fillRect(marginHorizontal + chartWidth + 1, scaleTop - 1, marginHorizontal, height);
        context.stroke();
    }

    let context = canvas.getContext('2d');
    if (context === null) {
        console.warn("context is null.");
        return;
    }
    context.beginPath();
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.stroke();

    if (option.drawScale) {
        drawScale(chartScale, context);
    }
    drawChart(itoDrawings, chartScale.koma, chartScale.tobi, resolution, context);
    cutoffSide(context);
}

export default {
    draw: draw,
};
